/**
 * Search hook for v2v2 SWFL Farmers Market Application
 * Manages search state with debounced queries and filter management
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  SearchResult, 
  SearchFilters, 
  UseSearchReturn,
  SEASON_MONTHS,
  SWFL_LOCATIONS
} from '@/types/search';
import { 
  buildSearchIndex, 
  searchProducts, 
  getSearchCategories, 
  getSearchSuggestions 
} from '@/lib/search-index';

// Debounce utility
function debounce<T extends (...args: Parameters<T>) => ReturnType<T>>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };
    
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

export function useSearch(): UseSearchReturn {
  // Search state
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFiltersState] = useState<SearchFilters>({});
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isIndexBuilt, setIsIndexBuilt] = useState(false);

  // Initialize search index
  useEffect(() => {
    let mounted = true;
    
    const initSearch = async () => {
      try {
        await buildSearchIndex();
        if (mounted) {
          setIsIndexBuilt(true);
        }
      } catch (err) {
        if (mounted) {
          setError('Failed to initialize search index');
          console.error(err);
        }
      }
    };
    
    initSearch();
    
    return () => {
      mounted = false;
    };
  }, []);

  // Perform search
  const performSearch = useCallback((searchQuery: string, searchFilters: SearchFilters) => {
    if (!isIndexBuilt) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const searchResults = searchProducts(searchQuery, {
        filters: searchFilters,
        limit: 100,
      });
      
      setResults(searchResults);
      setTotalCount(searchResults.length);
    } catch (err) {
      setError('Search failed');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [isIndexBuilt]);

  // Debounced search function
  const debouncedSearch = useMemo(
    () => debounce((searchQuery: string, searchFilters: SearchFilters) => {
      performSearch(searchQuery, searchFilters);
    }, 300),
    [performSearch]
  );

  // Update query and trigger search
  const handleSetQuery = useCallback((newQuery: string) => {
    setQuery(newQuery);
    debouncedSearch(newQuery, filters);
  }, [filters, debouncedSearch]);

  // Update filters and trigger search
  const setFilters = useCallback((newFilters: SearchFilters) => {
    setFiltersState(newFilters);
    debouncedSearch(query, newFilters);
  }, [query, debouncedSearch]);

  // Update a single filter
  const updateFilter = useCallback(<K extends keyof SearchFilters>(
    key: K,
    value: SearchFilters[K]
  ) => {
    const newFilters = { ...filters, [key]: value };
    setFiltersState(newFilters);
    debouncedSearch(query, newFilters);
  }, [query, filters, debouncedSearch]);

  // Clear search
  const clearSearch = useCallback(() => {
    setQuery('');
    setResults([]);
    setTotalCount(0);
    setError(null);
  }, []);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFiltersState({});
    debouncedSearch(query, {});
  }, [query, debouncedSearch]);

  // Group results by type
  const groupedResults = useMemo(() => ({
    products: results.filter(r => r.type === 'product'),
    vendors: results.filter(r => r.type === 'vendor'),
    markets: results.filter(r => r.type === 'market'),
  }), [results]);

  // Get available filter options
  const filterOptions = useMemo(() => ({
    categories: getSearchCategories(),
    seasons: Object.keys(SEASON_MONTHS),
    locations: SWFL_LOCATIONS.map(loc => loc.name),
  }), []);

  // Get active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.categories?.length) count += filters.categories.length;
    if (filters.seasons?.length) count += filters.seasons.length;
    if (filters.locations?.length) count += filters.locations.length;
    if (filters.vendorTypes?.length) count += filters.vendorTypes.length;
    if (filters.priceRange?.min || filters.priceRange?.max) count += 1;
    if (filters.hasUPick) count += 1;
    if (filters.isOrganic) count += 1;
    if (filters.isFeatured) count += 1;
    return count;
  }, [filters]);

  return {
    query,
    setQuery: handleSetQuery,
    results,
    isLoading: isLoading || (!isIndexBuilt && results.length === 0),
    filters,
    setFilters,
    updateFilter,
    clearSearch,
    clearFilters,
    totalCount,
    groupedResults,
    filterOptions,
    activeFilterCount,
    error,
    isIndexBuilt,
  };
}

// Hook for getting search suggestions
export function useSearchSuggestions(query: string) {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!query.trim() || query.length < 2) {
      setSuggestions([]);
      return;
    }

    let mounted = true;
    setIsLoading(true);

    const getSuggestions = async () => {
      try {
        await buildSearchIndex();
        if (mounted) {
          const results = getSearchSuggestions(query, 5);
          setSuggestions(results);
        }
      } catch (err) {
        if (mounted) {
          console.error('Failed to get suggestions:', err);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    const timeoutId = setTimeout(getSuggestions, 200);

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
    };
  }, [query]);

  return { suggestions, isLoading };
}
